import type { useTranslations } from 'next-intl'

import type { ChatCostEstimate } from '@/lib/billing-types'

/**
 * Rounding for the out-of-credits estimate. The inputs are a 30-day average,
 * so printing "1,274 credits" or "568 chats" claims a precision the number does
 * not have — the dialog shows the size and the AI/tool proportion, nothing finer.
 */

/** 30, 300, 1.3k, 12k — the copy around it already says "about". */
export function approxCredits(credits: number): string {
  if (credits < 50) return `${Math.max(1, Math.round(credits))}`
  if (credits < 1_000) return `${Math.round(credits / 50) * 50}`
  if (credits < 10_000) return `${(Math.round(credits / 100) / 10).toFixed(1)}k`
  return `${Math.round(credits / 1_000)}k`
}

/**
 * Under 1 stays under 1 (the plan cannot fund one chat like these), exact under
 * 10, then to the nearest 5, then two significant figures.
 */
export function approxChats(chats: number): number {
  if (chats < 1) return 0
  if (chats < 10) return Math.floor(chats)
  if (chats < 100) return Math.round(chats / 5) * 5
  const magnitude = 10 ** (Math.floor(Math.log10(chats)) - 1)
  return Math.round(chats / magnitude) * magnitude
}

/** Tool share of a chat's spend, to the nearest 5%. */
export function toolSharePercent(estimate: ChatCostEstimate): number {
  if (estimate.creditsPerChat <= 0) return 0
  return (
    Math.round((estimate.toolCreditsPerChat / estimate.creditsPerChat) * 20) * 5
  )
}

/**
 * "A chat uses about 1.3k credits, roughly 70% AI and 30% tools." Under 5% either
 * way it says so in words — "100% AI and 0% tools" reads like a bug.
 */
export function chatCostLine(
  estimate: ChatCostEstimate,
  t: ReturnType<typeof useTranslations<'Billing'>>,
): string {
  const total = approxCredits(estimate.creditsPerChat)
  const toolShare = toolSharePercent(estimate)
  if (toolShare < 5) return t('exhaustedChatCostAiOnly', { total })
  if (toolShare > 95) return t('exhaustedChatCostToolsOnly', { total })
  return t('exhaustedChatCost', { total, ai: 100 - toolShare, tool: toolShare })
}
